import {
  Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, MenuItem, TextField, Typography, Divider, Alert,
  Radio, RadioGroup, IconButton, Tooltip, SelectChangeEvent,
  Table, TableHead, TableBody, TableRow, TableCell, Autocomplete, InputAdornment,
  Drawer as MuiDrawer
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';
import CustomDataGrid from '../DataGrid/DataGrid';
import { GridValueGetterParams, GridColDef } from "@mui/x-data-grid";
import { Utils } from '../../utils/common-utils';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { calculateSalary, processSalary } from '../../store/salaryStore/salaryThunk';
import { clearSalaryCalculation } from '../../store/salaryStore/salarySlice';
import { fetchExpensePayments } from '../../store/expenseStore/expenseThunk';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchCostCentres } from '../../store/costCentreStore/costCentreThunk';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  fetchSalaryHeadMaster as fetchSalaryHeadMasterThunk,
  fetchEmployeeSalaryConfig as fetchEmployeeSalaryConfigThunk,
  fetchSalaryGradeById,
  fetchEmployeeTimesheetMonthly,
  fetchEmployeeDailyAttendance,
  fetchHolidaysInRange,
  fetchEmployeeLeaveRequests
} from '../../store/payrollStore/payrollThunk';
import { fetchWeekOffs } from '../../store/hrmsStore/hrmsThunk';
import { decryptSalaryConfig, decryptSalaryGrade } from '../../utils/salaryCrypto';

interface DialogProcessSalaryFormProps {
  title: String;
  open: boolean;
  onClose: (refresh: any) => void;
}

interface ITdsOption extends IOptions {
  rate?: number;
  taxSection?: string;
  taxName?: string;
}

const DialogProcessSalaryForm: FC<DialogProcessSalaryFormProps> = ({ onClose, open, title }) => {

  const configFetchTokenRef = React.useRef(0);

  const { status, salaryCalculation } = useAppSelector(state => state.salaries);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { employees } = useAppSelector(state => state.employees);
  const { costCentres } = useAppSelector(state => state.costCentres);
  const dispatch = useAppDispatch();

  // Form states
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [workingDays, setWorkingDays] = useState(0);
  const [presentDays, setPresentDays] = useState(0);
  const [lopDays, setLopDays] = useState(0);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState<{
    present: number; absent: number; leave: number; halfDay: number; holiday: number; weekOff: number; compOff: number; totalRecords: number;
  } | null>(null);
  const [attendanceDays, setAttendanceDays] = useState<any[]>([]);
  // Per-date leave status map (YYYY-MM-DD -> 'approved' | 'pending' | 'rejected'),
  // built from the same leave-request source the mobile app calendar uses.
  const [leaveByDate, setLeaveByDate] = useState<Record<string, string>>({});
  // Per-date leave-type name (YYYY-MM-DD -> e.g. "Sick Leave"), so the timesheet
  // detail can show the leave the employee actually applied for, not just "Leave".
  const [leaveNameByDate, setLeaveNameByDate] = useState<Record<string, string>>({});
  // Per-date leave reason/comment (YYYY-MM-DD -> e.g. "fever, doctor visit") the
  // employee wrote while applying — shown in the calendar tooltip and detail table.
  const [leaveReasonByDate, setLeaveReasonByDate] = useState<Record<string, string>>({});
  // Per-date holiday name (YYYY-MM-DD -> e.g. "Diwali"), from the holiday config —
  // same source as the app calendar's holiday list — so we can label holiday days.
  const [holidayNameByDate, setHolidayNameByDate] = useState<Record<string, string>>({});
  // Selected employee's assigned week-off days ([{day, weeks}]) — null = no pattern (Sunday default)
  const [empWeekOffDays, setEmpWeekOffDays] = useState<any[] | null>(null);
  const [timesheetStatus, setTimesheetStatus] = useState<string | null>(null);
  const [timesheetTotalHours, setTimesheetTotalHours] = useState<number>(0);
  const [attendanceDrawerOpen, setAttendanceDrawerOpen] = useState(false);
  const [salaryConfigLoaded, setSalaryConfigLoaded] = useState(false);
  const [defaultPaySchedule, setDefaultPaySchedule] = useState<any>(null);
  const [selectedSalaryConfig, setSelectedSalaryConfig] = useState<any>(null);

  // Salary component states
  const [basicSalary, setBasicSalary] = useState<string>('');
  const [hra, setHra] = useState<string>('');

  // Salary head master options (fetched once on open)
  const [earningHeadOptions, setEarningHeadOptions] = useState<any[]>([]);
  const [deductionHeadOptions, setDeductionHeadOptions] = useState<any[]>([]);

  // Additional earnings states (dynamic fields only)
  const [additionalEarnings, setAdditionalEarnings] = useState<Array<{label: string, amount: string}>>([]);
  const [configuredDeductions, setConfiguredDeductions] = useState<Array<{label: string, amount: string}>>([]);

  // Expense add-ons (approved expenses for selected employee)
  const [approvedExpenses, setApprovedExpenses] = useState<any[]>([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<string>>(new Set());
  const [showExpenseAddons, setShowExpenseAddons] = useState(false);

  // Bank account states
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);

  // Cost centre state
  const [selectedCostCentre, setSelectedCostCentre] = useState('');

  // Payment mode states
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [paymentStatus, setPaymentStatus] = useState('PROCESSED');
  const [transactionRef, setTransactionRef] = useState('');

  // Tax configuration states.
  // Statutory deductions default OFF: nothing should be deducted unless the
  // employee's salary config defines the head, or the admin explicitly toggles
  // it on here. An empty config must give Net Take Home = Gross.
  const [applyTax, setApplyTax] = useState(true);
  const [applyPF, setApplyPF] = useState(false);
  const [applyESI, setApplyESI] = useState(false);
  const [applyPT, setApplyPT] = useState(false);
  const [applyTDS, setApplyTDS] = useState(false);

  // TDS states
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<ITdsOption[]>([]);
  const [tdsSelected, setTdsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  });

  // Form validation schema
  const schema = yup.object().shape({
    employeeId: yup.string().required("Employee is required."),
    month: yup.string().required("Salary month is required."),
  });

  const methods = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema)
  });

  // Load TDS rates once when form opens
  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const getTdsRates = async () => {
      try {
        const taxRatesData: any = await dispatch(fetchTaxRates()).unwrap();
        if (taxRatesData && taxRatesData.length > 0 && isMounted) {
          const tdsRates = taxRatesData.filter((item: any) => item.taxType === "tds");
          const tdsOptions = tdsRates.map((item: any) => ({
            label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
            value: item._id,
            rate: item.taxRate,
            taxSection: item.taxSection,
            taxName: item.taxName
          }));
          setTdsTaxRateOptions(tdsOptions);
        }
      } catch (error) {
        console.error('Error fetching TDS rates:', error);
      }
    };

    getTdsRates();

    // Load salary head master for label autocomplete
    fetchSalaryHeadMaster().then((heads: any[]) => {
      if (!isMounted) return;
      const deductionTypes = ['Deduction', 'Employer Contribution'];
      const earning = heads
        .filter(h => !deductionTypes.includes(h.componentType || '') && h.isActive !== false);
      const deduction = heads
        .filter(h => deductionTypes.includes(h.componentType || '') && h.isActive !== false);
      setEarningHeadOptions(earning);
      setDeductionHeadOptions(deduction);
    });

    return () => {
      isMounted = false;
    };
  }, [dispatch, open]);

  // Load employees, banks, and cost centres when form opens
  useEffect(() => {
    if (!open || !selectedAccount?._id) return;

    let isMounted = true;

    const loadData = async () => {
      try {
        // Load employees (high limit so Autocomplete sees full list)
        await dispatch(fetchEmployees({
          accountId: selectedAccount._id,
          type: 'list',
          params: 'limit=5000&page=1&status=ACTIVE'
        }));

        // Load bank accounts
        const bankData: any = await dispatch(fetchBanks(selectedAccount._id));
        if (bankData.payload?.banks?.length > 0 && isMounted) {
          const bankOptions = bankData.payload.banks.map((c: any) => ({
            label: c.accountName + '-' + c.bankName,
            value: c._id
          }));
          setFromBankAccountOptions(bankOptions);

          const firstBank = bankData.payload.banks[0];
          methods.setValue('fromBankAccount', firstBank._id);
          setSelectedFromBankAccount(firstBank._id);
        }

        // Load cost centres (only those enabled for payroll)
        await dispatch(fetchCostCentres({ activeOnly: true, useForPayroll: true }));

        // Default workingDays = 26 (PaySchedule removed)
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [selectedAccount?._id, open, dispatch]);

  // Calculate expense add-on total
  const expenseAddOnTotal = useMemo(() => {
    return approvedExpenses
      .filter(e => selectedExpenseIds.has(e._id))
      .reduce((sum, e) => sum + Number(e.settleAmount ?? e.amount ?? 0), 0);
  }, [approvedExpenses, selectedExpenseIds]);

  const roundMoney = (value: number) => Number((Number(value) || 0).toFixed(2));

  // Calculate gross salary. When the config defines earning heads (Basic/HRA/…)
  // their sum is authoritative. When no breakup is configured, fall back to the
  // annual CTC / 12 so an employee with only a CTC set still gets a real gross
  // instead of ₹0.
  const calculateGrossSalary = () => {
    const total = additionalEarnings.reduce((sum, earning) => {
      return sum + (parseFloat(earning.amount) || 0);
    }, 0);
    if (total > 0) return roundMoney(total);
    return roundMoney(getMonthlyCtc(selectedSalaryConfig));
  };

  // Per-day rate — gross salary / working days
  const perDayRate = () => {
    const wd = Number(workingDays) || 26;
    return calculateGrossSalary() / wd;
  };

  // LOP deduction = per-day × LOP days
  const calculateLOPDeduction = () => {
    return Number((perDayRate() * (Number(lopDays) || 0)).toFixed(2));
  };

  // Prorated gross (after LOP)
  const calculateProratedGross = () => {
    return roundMoney(Math.max(0, calculateGrossSalary() - calculateLOPDeduction()));
  };

  const calculateConfiguredDeductions = () => {
    const total = configuredDeductions.reduce((sum, deduction) => {
      return sum + (parseFloat(deduction.amount) || 0);
    }, 0);
    return roundMoney(total);
  };

  const calculateManualNetSalary = () => {
    return roundMoney(Math.max(0, calculateProratedGross() - calculateConfiguredDeductions()));
  };

  const calculateManualPayable = () => {
    return roundMoney(calculateManualNetSalary() + expenseAddOnTotal);
  };

  const monthRange = (m: string) => {
    const [y, mm] = m.split('-').map(Number);
    const daysInMonth = new Date(y, mm, 0).getDate();
    // Build the boundary strings in LOCAL time. Going through toISOString() would
    // convert local midnight to UTC and (in IST, UTC+5:30) shift both ends back a
    // day — e.g. June 1 → 2026-05-31 — which pulls an adjacent-month holiday into
    // the count and over-reports holidays. Format the Y-M-D directly instead.
    const pad = (n: number) => String(n).padStart(2, '0');
    const startDate = `${y}-${pad(mm)}-01`;
    const endDate = `${y}-${pad(mm)}-${pad(daysInMonth)}`;
    return { startDate, endDate, daysInMonth };
  };

  const computeFromHead = (head: any, basicVal: number, ctcVal: number) => {
    if (!head) return 0;
    const pct = Number(head.percentage || 0);
    switch (head.calculationType) {
      case 'Fixed Amount': return Number(head.defaultAmount || 0);
      case 'Percentage of Basic': return Number(((basicVal * pct) / 100).toFixed(2));
      case 'Percentage of CTC': return Number(((ctcVal * pct) / 100).toFixed(2));
      case 'Manual':
      default: return Number(head.defaultAmount || 0);
    }
  };

  const fetchSalaryHeadMaster = async () => {
    try {
      return await dispatch(fetchSalaryHeadMasterThunk(200)).unwrap();
    } catch (err) {
      console.error('salaryHead master fetch failed', err);
      return [];
    }
  };

  const getDecimalValue = (value: any) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'object' && value.$numberDecimal !== undefined) return Number(value.$numberDecimal || 0);
    return Number(value || 0);
  };

  const normalizeHeadItems = (items: any[] = [], keepZero = false) => {
    return items
      .map((item: any) => ({
        label: String(item.head || item.headName || item.label || '').trim(),
        amount: String(getDecimalValue(item.value ?? item.amount))
      }))
      .filter(item => item.label && (keepZero || Number(item.amount) > 0));
  };

  const getMonthlyCtc = (cfg: any) => {
    const annualCtc = getDecimalValue(cfg?.ctcAmount);
    return annualCtc > 0 ? Number((annualCtc / 12).toFixed(2)) : 0;
  };

  const normalizeWholeNumberInput = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (!digitsOnly) return '';
    return String(Number(digitsOnly));
  };

  const applyHeadsFromMaster = (heads: any[], ctcGuess: number) => {
    // Find Basic head first
    const earningHeads = heads.filter(h => (h.componentType || 'Earning') === 'Earning' && h.isActive !== false);
    const basicHead = earningHeads.find(h => /basic/i.test(h.headName) || /basic/i.test(h.alias || ''));
    const hraHead = earningHeads.find(h => /hra|house\s*rent/i.test(h.headName) || /hra/i.test(h.alias || ''));

    const basicVal = basicHead ? computeFromHead(basicHead, 0, ctcGuess) : (ctcGuess ? Number((ctcGuess * 0.6).toFixed(2)) : 0);
    const hraVal = hraHead ? computeFromHead(hraHead, basicVal, ctcGuess) : (ctcGuess && !basicHead ? Number((ctcGuess * 0.4).toFixed(2)) : 0);

    if (basicVal > 0) setBasicSalary(String(basicVal));
    if (hraVal > 0) setHra(String(hraVal));

    const others = earningHeads.filter(h => h !== basicHead && h !== hraHead);
    const otherEarnings = others
      .map(h => ({ label: h.headName, amount: String(computeFromHead(h, basicVal, ctcGuess)) }))
      .filter(o => Number(o.amount) > 0);
    if (otherEarnings.length) setAdditionalEarnings(otherEarnings);
  };

  const calcHeadAmount = (head: any, basic: number, gross: number): number => {
    if (!head) return 0;
    const ct = head.calculationType;
    const pct = Number(head.percentage || 0);
    if (ct === 'Fixed Amount') return Number(head.defaultAmount || 0);
    if (ct === 'Percentage of Basic') return Number((pct / 100 * basic).toFixed(2));
    if (ct === 'Percentage of CTC' || ct === 'Percentage of Gross') return Number((pct / 100 * gross).toFixed(2));
    return 0;
  };

  const fetchEmployeeSalaryConfig = async (employeeId: string) => {
    const token = ++configFetchTokenRef.current;
    try {
      const rawList: any[] = await dispatch(fetchEmployeeSalaryConfigThunk({ employeeId, limit: 10, sortLatest: true })).unwrap();
      // Salary-config amounts are encrypted by the API — decrypt before use.
      const list: any[] = (rawList || []).map((cfg: any) => decryptSalaryConfig(cfg));
      if (token !== configFetchTokenRef.current) return null; // stale fetch, newer one fired
      // Sort by effectiveFrom DESC, fallback to updatedAt — pick latest config
      const sorted = [...list].sort((a: any, b: any) => {
        const da = new Date(a.effectiveFrom || a.updatedAt || 0).getTime();
        const db = new Date(b.effectiveFrom || b.updatedAt || 0).getTime();
        return db - da;
      });
      const cfg = sorted.find((c: any) => (c.employee?._id || c.employee) === employeeId) || sorted[0];
      if (token !== configFetchTokenRef.current) return null;

      if (!cfg) {
        setSalaryConfigLoaded(false);
        setSelectedSalaryConfig(null);
        setBasicSalary('');
        setHra('');
        setAdditionalEarnings([]);
        setConfiguredDeductions([]);
        return null;
      }

      setSelectedSalaryConfig(cfg);
      const earnings = normalizeHeadItems(cfg.companyAllowanceHeads || []);
      const deductions = normalizeHeadItems(cfg.deductionsHeads || [], true);
      const basicHead = earnings.find((e: any) => /basic/i.test(e.label));
      const hraHead = earnings.find((e: any) => /hra|house\s*rent/i.test(e.label));
      const others = earnings.filter((e: any) => !/basic|hra|house\s*rent/i.test(e.label));

      // Salary config is the source of truth: only surface Basic/HRA when the
      // config actually defines them. No CTC-based 50%/40% synthesis — an empty
      // config must stay empty on Process Salary (was auto-filling ghost heads).
      const basicAmt = basicHead ? String(basicHead.amount || '') : '';
      const basicNum = Number(basicAmt || 0);
      const hraAmt = hraHead ? String(hraHead.amount || '') : '';
      // Keep state vars in sync for backend payload
      setBasicSalary(basicAmt);
      setHra(hraAmt);
      const otherEnriched = others.map((o: any) => {
        if (Number(o.amount) > 0) return o;
        const head = (Array.isArray(earningHeadOptions) ? earningHeadOptions : []).find((h: any) => h.headName === o.label);
        if (!head) return o;
        const gross = basicNum + Number(hraAmt || 0);
        return { ...o, amount: String(calcHeadAmount(head, basicNum, gross)) };
      });
      // Merge Basic + HRA into unified earnings list (top of list)
      const merged = [
        ...(basicHead || basicAmt ? [{ label: basicHead?.label || 'Basic', amount: basicAmt }] : []),
        ...(hraHead || hraAmt ? [{ label: hraHead?.label || 'HRA', amount: hraAmt }] : []),
        ...otherEnriched
      ];
      setAdditionalEarnings(merged);
      const totalGross = basicNum + Number(hraAmt || 0) + otherEnriched.reduce((s: number, e: any) => s + Number(e.amount || 0), 0);
      const dedEnriched = deductions.map((d: any, idx: number) => {
        if (Number(d.amount) > 0) return d;
        // Try raw config item first (has calculationType/percentage saved)
        const rawItem = (cfg.deductionsHeads || [])[idx];
        if (rawItem) {
          const calc = calcHeadAmount(rawItem, basicNum, totalGross);
          if (calc > 0) return { ...d, amount: String(calc) };
        }
        // Fallback: lookup from head master options
        const head = (Array.isArray(deductionHeadOptions) ? deductionHeadOptions : []).find((h: any) => h.headName === d.label);
        if (!head) return d;
        return { ...d, amount: String(calcHeadAmount(head, basicNum, totalGross)) };
      }).filter((d: any) => d.label);
      setConfiguredDeductions(dedEnriched);

      setSalaryConfigLoaded(true);
      return cfg;
    } catch (err) {
      console.error('employeeSalaryConfig fetch failed', err);
      setSalaryConfigLoaded(false);
      setSelectedSalaryConfig(null);
      setConfiguredDeductions([]);
      return null;
    }
  };

  // Week-off comes from the employee's assigned Week-Off pattern (HRMS config),
  // shape: [{ day: 'Sat', weeks: [1,2,3,4,5] }] where weeks = nth occurrence of
  // that weekday in the month. Each employee can have a different pattern.
  const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const isWeekOffDate = (y: number, mm: number, d: number, pattern?: any[] | null): boolean => {
    const dow = new Date(y, mm - 1, d).getDay();
    const days: any[] = Array.isArray(pattern) ? pattern : [];
    // No pattern assigned → default Sunday week-off (previous behaviour)
    if (days.length === 0) return dow === 0;
    const entry = days.find((w: any) => String(w?.day || '').slice(0, 3).toLowerCase() === DAY_KEYS[dow]);
    if (!entry) return false;
    const weeks: number[] = Array.isArray(entry.weeks) ? entry.weeks.map(Number) : [];
    const occurrence = Math.ceil(d / 7);
    return weeks.length === 0 || weeks.includes(occurrence);
  };

  // Resolve the selected employee's week-off pattern: it may arrive populated on
  // the employee row, or as an id that must be looked up in the week-off configs.
  const resolveWeekOffDays = async (employeeId: string): Promise<any[] | null> => {
    try {
      const row: any = employees.find((emp: any) => emp._id === employeeId || emp.employee?._id === employeeId);
      const empData: any = row?.employee || row;
      const assigned = empData?.weekOffPattern ?? row?.weekOffPattern;
      if (!assigned) return null;
      if (Array.isArray(assigned?.weekOffDays) || Array.isArray(assigned?.weekOffs)) {
        return assigned.weekOffDays || assigned.weekOffs;
      }
      const assignedId = String(assigned?._id || assigned?.id || assigned);
      if (!assignedId || assignedId === 'null' || assignedId === 'undefined' || assignedId === '[object Object]') return null;
      const res: any = await dispatch(fetchWeekOffs()).unwrap();
      const list: any[] = res?.data || res || [];
      const cfg = list.find((w: any) => String(w._id || w.id) === assignedId);
      return cfg ? (cfg.weekOffDays || cfg.weekOffs || null) : null;
    } catch {
      return null;
    }
  };

  const fetchAttendanceForMonth = async (employeeId: string, mo: string) => {
    if (!selectedAccount?._id || !mo) return;
    setAttendanceLoading(true);
    try {
      const { startDate, endDate, daysInMonth } = monthRange(mo);
      const [yy, mm] = mo.split('-').map(Number);

      // Per-employee week-off pattern (same source the mobile app calendar uses)
      const woDays = await resolveWeekOffDays(employeeId);
      setEmpWeekOffDays(woDays);

      let payload: any = null;
      try {
        // Primary: timesheet monthly (source of truth — HRMS manager/employee marks here)
        payload = await dispatch(fetchEmployeeTimesheetMonthly({ employeeId, year: yy, month: mm })).unwrap();
      } catch (tsErr) {
        try {
          // Fallback: daily attendance API
          payload = await dispatch(fetchEmployeeDailyAttendance({ accountId: selectedAccount._id, startDate, endDate, employeeId })).unwrap();
        } catch (retryErr) {
          setAttendanceSummary(null);
          setWorkingDays(daysInMonth);
          setPresentDays(daysInMonth);
          setLopDays(0);
          setAttendanceLoading(false);
          return;
        }
      }

      payload = payload || {};
      const days: any[] = Array.isArray(payload.days) ? payload.days : [];

      // Fetch leave requests separately (same source as the mobile app calendar) and
      // build a per-date status map. The monthly timesheet marks BOTH approved and
      // pending leaves as type:'Leave', which over-reports Leave. This map lets us
      // treat only approved leave as paid Leave; pending/rejected → Absent.
      const leaveMap: Record<string, string> = {};
      const leaveNameMap: Record<string, string> = {};
      const leaveReasonMap: Record<string, string> = {};
      try {
        const leaveReqs: any[] = await dispatch(fetchEmployeeLeaveRequests({ employeeId })).unwrap();
        const rank: Record<string, number> = { approved: 3, pending: 2, rejected: 1 };
        (leaveReqs || []).forEach((lr: any) => {
          const status = String(lr.status || '').toLowerCase();
          if (!['approved', 'pending', 'rejected'].includes(status)) return;
          // Name of the leave type applied for (e.g. "Sick Leave").
          const name = String(lr.leaveType || lr.leaveConfig?.name || '').trim();
          // Reason/comment the employee wrote while applying (field name varies by API).
          const reason = String(lr.reason || lr.description || lr.remarks || lr.comment || lr.note || '').trim();
          const dts: string[] = Array.isArray(lr.dates) ? lr.dates : [];
          dts.forEach((raw: any) => {
            const key = String(raw).slice(0, 10);
            if (!key) return;
            // Keep the strongest status if multiple requests touch the same date.
            if (!leaveMap[key] || (rank[status] || 0) > (rank[leaveMap[key]] || 0)) {
              leaveMap[key] = status;
              if (name) leaveNameMap[key] = name;
              if (reason) leaveReasonMap[key] = reason;
            }
          });
        });
      } catch (e) {
        // Leave request fetch failed — fall back to per-day leaveStatus from timesheet.
      }
      setLeaveByDate(leaveMap);
      setLeaveNameByDate(leaveNameMap);
      setLeaveReasonByDate(leaveReasonMap);

      // Enrich each Leave day with the authoritative status from the leave-request map
      // (falls back to the day's own leaveStatus when the request list is unavailable).
      days.forEach((d: any) => {
        if (String(d.type || '').trim() === 'Leave') {
          const key = String(d.date).slice(0, 10);
          d.leaveStatus = leaveMap[key] || d.leaveStatus || 'pending';
        }
      });

      setAttendanceDays(days);
      setTimesheetStatus(payload.status || null);
      setTimesheetTotalHours(Number(payload.totalRegularHours || 0) + Number(payload.totalOvertimeHours || 0));

      if (days.length === 0) {
        setAttendanceSummary(null);
        setWorkingDays(daysInMonth);
        setPresentDays(daysInMonth);
        setLopDays(0);
        setAttendanceLoading(false);
        return;
      }

      // Count by exact type enum
      const summary = { present: 0, absent: 0, leave: 0, halfDay: 0, holiday: 0, weekOff: 0, compOff: 0, totalRecords: days.length };
      days.forEach((d: any) => {
        const t = String(d.type || '').trim();
        if (t === 'Present' || t === 'WFH' || t === 'Overtime') summary.present++;
        else if (t === 'Half-Day') summary.halfDay++;
        else if (t === 'Absent') summary.absent++;
        // Comp-Off = paid off earned by working on a week-off/holiday (type spelling varies)
        else if (/^comp[\s-]?off$/i.test(t)) summary.compOff++;
        else if (t === 'Leave') {
          // Only approved leave counts (paid). Pending/rejected leave = unpaid → treated as Absent.
          if (String(d.leaveStatus || '').toLowerCase() === 'approved') summary.leave++;
          else summary.absent++;
        }
        else if (t === 'Holiday') summary.holiday++;
        else if (t === 'WeekOff' || t === 'Optional-Holiday') summary.weekOff++;
      });

      // Fetch holidays from holiday API for the month
      const holidayDateSet = new Set<string>();
      try {
        const hData: any = await dispatch(fetchHolidaysInRange({ startDate, endDate })).unwrap();
        const holidays = Array.isArray(hData) ? hData : (hData?.holidays || hData?.data || []);
        if (Array.isArray(holidays) && holidays.length > 0) {
          // Count unique holiday dates strictly within the month. Compare as plain
          // YYYY-MM-DD strings — startDate/endDate are already local date strings, so
          // slicing the raw holiday date the same way avoids any UTC shift that would
          // let an adjacent-month holiday leak in (which over-reported the count).
          const nameMap: Record<string, string> = {};
          holidays.forEach((h: any) => {
            const raw = h.date || h.holidayDate || h.dateFrom;
            if (!raw) return;
            const key = String(raw).slice(0, 10);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
            if (key < startDate || key > endDate) return;
            // Only Mandatory/Default holidays are paid days off and count as "Holiday".
            // Optional holidays are opt-in (the app calendar doesn't mark them either),
            // so they must NOT inflate the Holiday count — e.g. an Optional "hune 21"
            // was making June show 2 holidays when only 1 Mandatory holiday exists.
            const type = String(h.holidayType || h.type || '').toLowerCase();
            if (type === 'optional') return;
            holidayDateSet.add(key);
            const hName = String(h.name || h.title || h.holidayName || '').trim();
            if (hName) nameMap[key] = hName;
          });
          summary.holiday = holidayDateSet.size;
          setHolidayNameByDate(nameMap);
        } else {
          summary.holiday = 0;
          setHolidayNameByDate({});
        }
      } catch (e) {
        // ignore holiday fetch fail
        setHolidayNameByDate({});
      }

      // Days with no attendance record that fall on the employee's week-off pattern
      // count as Week-Off (explicit WeekOff records are already counted above;
      // holiday-config dates take priority — matches the day-wise calendar cells).
      const recordedDates = new Set(days.map((d: any) => String(d.date).slice(0, 10)));
      for (let d = 1; d <= daysInMonth; d++) {
        const key = `${yy}-${String(mm).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        if (recordedDates.has(key) || holidayDateSet.has(key)) continue;
        if (isWeekOffDate(yy, mm, d, woDays)) summary.weekOff++;
      }

      setAttendanceSummary(summary);

      // Working days = total days in month (calendar)
      const wd = daysInMonth;

      // Present: Present + 0.5*HalfDay + Leave (paid) + WeekOff + Holiday + CompOff all count as paid
      const paidDays = summary.present + summary.halfDay * 0.5 + summary.leave + summary.weekOff + summary.holiday + summary.compOff;
      const computedPresent = Math.min(wd, paidDays);
      setWorkingDays(wd);
      setPresentDays(Math.round(computedPresent));
      setLopDays(Math.max(0, wd - Math.round(computedPresent)));
    } catch (err) {
      console.error('attendance fetch failed', err);
      setAttendanceSummary(null);
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Watch for employee selection changes
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'employeeId' && value.employeeId) {
        const selectedEmployee = employees.find((emp: any) => emp._id === value.employeeId);
        if (selectedEmployee) {
          const empData: any = selectedEmployee.employee || selectedEmployee;

          // Clear stale salary calculation from previous employee
          dispatch(clearSalaryCalculation());

          // EmployeeSalaryConfig is sole source. No fallback.
          fetchEmployeeSalaryConfig(value.employeeId);

          // 2. Pull attendance for current month
          fetchAttendanceForMonth(value.employeeId, month);

          // Auto-populate cost centre from employee's default
          if (empData.costCentre) {
            setSelectedCostCentre(empData.costCentre);
          }

          // Fetch approved expenses for this employee
          const loadApprovedExpenses = async () => {
            try {
              const result: any = await dispatch(fetchExpensePayments({
                employee: value.employeeId,
                paymentStatus: 'APPROVED'
              })).unwrap();
              setApprovedExpenses(result?.payments || []);
              setSelectedExpenseIds(new Set());
            } catch (err) {
              setApprovedExpenses([]);
            }
          };
          loadApprovedExpenses();
        }
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods, employees, dispatch, month, defaultPaySchedule, selectedAccount?._id]);

  // Re-pull attendance when month changes (employee already selected). Else set calendar-based defaults.
  useEffect(() => {
    if (!month) return;
    const empId = methods.getValues('employeeId');
    if (empId) {
      fetchAttendanceForMonth(empId, month);
    } else {
      const [yy, mm] = month.split('-').map(Number);
      const totalDays = new Date(yy, mm, 0).getDate();
      setWorkingDays(totalDays);
      setPresentDays(totalDays);
      setLopDays(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // Re-fetch employee salary config every time form opens (avoid stale cached values)
  useEffect(() => {
    if (!open) return;
    const empId = methods.getValues('employeeId');
    if (empId) {
      fetchEmployeeSalaryConfig(empId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-change payment status to PAID when CASH is selected
  useEffect(() => {
    if (paymentMode === 'CASH' && paymentStatus === 'PROCESSED') {
      setPaymentStatus('PAID');
    }
  }, [paymentMode, paymentStatus]);

  // Memoize TDS options
  const tdsOptionsWithNoTds = useMemo(() => {
    return [{ label: 'Standard Slabs', value: '', rate: 0 }, ...tdsTaxRateOptions];
  }, [tdsTaxRateOptions]);

  // Handle TDS selection change
  const handleTdsChange = (event: any) => {
    const selectedTdsId = event.target.value;

    if (!selectedTdsId) {
      setTdsSelected({ _id: "", label: "Standard Slabs", rate: 0 });
      return;
    }

    const selectedTdsOption = tdsTaxRateOptions.find((opt: ITdsOption) => opt.value === selectedTdsId);
    if (selectedTdsOption) {
      setTdsSelected({
        _id: selectedTdsOption.value,
        label: selectedTdsOption.label,
        rate: selectedTdsOption.rate || 0
      });
    }
  };

  // Handle adding additional earning field
  const handleAddAdditionalEarning = () => {
    setAdditionalEarnings([...additionalEarnings, { label: '', amount: '' }]);
  };

  // Handle removing additional earning field
  const handleRemoveAdditionalEarning = (index: number) => {
    const updated = additionalEarnings.filter((_, i) => i !== index);
    setAdditionalEarnings(updated);
  };

  // Handle additional earning change
  const handleAdditionalEarningChange = (index: number, field: 'label' | 'amount', value: string) => {
    const updated = [...additionalEarnings];
    updated[index][field] = value;
    if (field === 'label') {
      const head = earningHeadOptions.find((h: any) => h.headName === value);
      if (head) {
        const basicNum = Number(basicSalary || 0);
        const gross = basicNum + Number(hra || 0);
        const calc = calcHeadAmount(head, basicNum, gross);
        if (calc > 0) updated[index].amount = String(calc);
      }
    }
    setAdditionalEarnings(updated);
    // Sync Basic/HRA state for backend payload
    const basicRow = updated.find(e => /^basic/i.test(e.label || ''));
    const hraRow = updated.find(e => /^hra|house\s*rent/i.test(e.label || ''));
    if (basicRow) setBasicSalary(basicRow.amount || '');
    if (hraRow) setHra(hraRow.amount || '');
  };

  const handleAddDeduction = () => {
    setConfiguredDeductions([...configuredDeductions, { label: '', amount: '' }]);
  };

  const handleRemoveDeduction = (index: number) => {
    setConfiguredDeductions(configuredDeductions.filter((_, i) => i !== index));
  };

  const handleDeductionChange = (index: number, field: 'label' | 'amount', value: string) => {
    const updated = [...configuredDeductions];
    updated[index][field] = value;
    if (field === 'label') {
      const head = deductionHeadOptions.find((h: any) => h.headName === value);
      if (head) {
        const basicNum = Number(basicSalary || 0);
        const gross = basicNum + Number(hra || 0) + additionalEarnings.reduce((s, e) => s + Number(e.amount || 0), 0);
        const calc = calcHeadAmount(head, basicNum, gross);
        if (calc > 0) updated[index].amount = String(calc);
      }
    }
    setConfiguredDeductions(updated);
  };

  // Backend calculate API disabled — Employee Salary Config values are source of truth.
  // Form displays raw values from config + Statutory Deductions panel computes locally.
  const handleCalculate = async () => { /* no-op */ };

  // Handle form submit
  const onSubmit = async (data: any) => {
    // Gross (from configured heads, or CTC/12 when no breakup) must be positive.
    // Basic itself is optional — a config with only a CTC still processes.
    if (calculateGrossSalary() <= 0) {
      alert('Gross salary is ₹0. Set an Annual CTC or add earning heads to the employee salary config.');
      return;
    }
    try {
      setFormSubmitted(true);

      const taxConfig = {
        applyTax,
        applyPF,
        applyESI,
        applyPT,
        tdsConfig: {
          apply: applyTDS,
          rate: tdsSelected.rate,
          label: tdsSelected.label
        }
      };

      const paymentOptions = {
        paymentStatus: paymentStatus as 'PROCESSED' | 'PAID',
        paymentMode,
        fromBankAccount: selectedFromBankAccount,
        instrument: transactionRef,
        remarks: data.remarks
      };

      const additionalEarningsData = additionalEarnings
        .filter(e => e.label && e.amount)
        .map(e => ({ label: e.label, amount: parseFloat(e.amount) }));
      const additionalDeductionsData = configuredDeductions
        .filter(e => e.label && e.amount)
        .map(e => ({ label: e.label, amount: parseFloat(e.amount) }));

      const processedSalary: any = await dispatch(processSalary({
        employeeId: data.employeeId,
        month,
        workingDays,
        presentDays,
        basicSalary: parseFloat(basicSalary) || 0,
        hra: parseFloat(hra) || 0,
        costCentre: selectedCostCentre || undefined,
        taxConfig,
        paymentOptions,
        additionalEarnings: additionalEarningsData,
        additionalDeductions: additionalDeductionsData,
        salaryConfigId: selectedSalaryConfig?._id,
        ctcAmount: getDecimalValue(selectedSalaryConfig?.ctcAmount),
        expenseIds: Array.from(selectedExpenseIds)
      })).unwrap();

      methods.reset();
      setFormSubmitted(false);
      const salaryId = processedSalary?.data?._id || processedSalary?._id || processedSalary?.salary?._id;
      onClose({ refresh: true, salaryId });
    } catch (error: any) {
      setFormSubmitted(false);
      console.error('Error processing salary:', error);
      const msg = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || 'Failed to process salary';
      alert(`Process Salary failed: ${msg}`);
    }
  };

  const handleClose = () => {
    methods.reset();
    setFormSubmitted(false);
    setAttendanceSummary(null);
    setTimesheetStatus(null);
    setTimesheetTotalHours(0);
    setAttendanceDays([]);
    setLeaveByDate({});
    setLeaveNameByDate({});
    setLeaveReasonByDate({});
    setHolidayNameByDate({});
    setEmpWeekOffDays(null);
    setSalaryConfigLoaded(false);
    dispatch(clearSalaryCalculation());
    setLopDays(0);
    setBasicSalary('');
    setHra('');
    setAdditionalEarnings([]);
    setConfiguredDeductions([]);
    setSelectedSalaryConfig(null);
    onClose(false);
  };

  return (
    <div className="drawer-form-container" style={{ width: "100%", maxWidth: "100%", height: "100%", background: "#f9fafb" }}>
      <FormProvider {...methods}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: 0,
            position: "relative"
          }}
          component="form"
          noValidate
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 0.8,
            bgcolor: 'transparent'
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#111827', fontFamily: 'Ubuntu' }}>
              Process Salary Payment
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant='outlined'
                startIcon={<CalendarMonthIcon fontSize="small" />}
                onClick={() => {
                  const empId = methods.getValues('employeeId');
                  if (!empId) { toast.warn('Select an employee first'); return; }
                  setAttendanceDrawerOpen(true);
                }}
                size="small"
                sx={{ height: '34px', textTransform: 'none' }}
              >
                View Attendance
              </Button>
              <Button
                variant='outlined'
                onClick={handleClose}
                size="small"
                sx={{ minWidth: '90px', height: '34px', textTransform: 'none' }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="button"
                onClick={methods.handleSubmit(onSubmit)}
                variant="contained"
                loading={formSubmitted || status === ApiStatus.LOADING}
                size="small"
                sx={{ minWidth: '150px', height: '34px', textTransform: 'none' }}
              >
                Process Salary
              </LoadingButton>
            </Box>
          </Box>

          <DialogContent sx={{
            px: 1.5,
            py: 1,
            paddingBottom: '16px',
            overflowY: 'auto',
            // Don't chain-scroll the page behind the drawer when this list hits its end
            overscrollBehavior: 'contain',
            flex: 1,
            bgcolor: '#f9fafb',
            fontFamily: 'Ubuntu',
            '& .c-text-field': {
              backgroundColor: '#fff !important',
              border: '1px solid #e2e8f0 !important',
              borderRadius: '8px !important',
              padding: '0 !important',
              transition: 'border-color 0.15s ease',
              '&:hover': { borderColor: '#94a3b8 !important' },
              '&:focus-within': { borderColor: '#64748b !important', borderWidth: '1.5px !important' },
            },
            '& .c-text-field .MuiInputBase-root': {
              height: 36,
              minHeight: 36,
              fontSize: 13,
              padding: '0 10px !important',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'transparent !important',
            },
            '& .c-text-field .MuiInputBase-input': {
              padding: '0 !important',
              height: 36,
              lineHeight: '36px',
              fontSize: 13,
              color: '#0f172a',
            },
            '& .c-text-field.MuiAutocomplete-root .MuiInputBase-root': {
              padding: '0 10px !important',
              height: 36,
              minHeight: 36,
            },
            '& .c-text-field.MuiAutocomplete-root .MuiAutocomplete-input': {
              padding: '0 !important',
              height: '34px !important',
              lineHeight: '34px !important',
            },
            '& .c-text-field input[type="date"]': {
              lineHeight: '36px',
              width: '100%',
              '&::-webkit-calendar-picker-indicator': { cursor: 'pointer', marginLeft: 'auto', opacity: 0.6 },
            },
            '& .c-label': { fontSize: 12, color: '#334155', margin: '0 0 5px 2px', fontWeight: 600, letterSpacing: '-0.01em' },
            '& .c-text-field-gray': {
              backgroundColor: '#f1f5f9 !important',
              border: '1px solid transparent !important',
              borderRadius: '8px !important',
              padding: '0 !important',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
              '&:hover': { borderColor: '#cbd5e1 !important' },
              '&:focus-within': { borderColor: '#94a3b8 !important', borderWidth: '1.5px !important', backgroundColor: '#fff !important' },
            },
            '& .c-text-field-gray .MuiInputBase-root': {
              height: 36, minHeight: 36, fontSize: 13, padding: '0 10px !important', display: 'flex', alignItems: 'center', backgroundColor: 'transparent !important',
            },
            '& .c-text-field-gray .MuiInputBase-input': {
              padding: '0 !important', height: 36, lineHeight: '36px', fontSize: 13, color: '#0f172a',
            },
            '& .c-text-field-gray.MuiAutocomplete-root .MuiInputBase-root': { padding: '0 10px !important', height: 36, minHeight: 36 },
            '& .c-text-field-gray.MuiAutocomplete-root .MuiAutocomplete-input': { padding: '0 !important', height: '34px !important', lineHeight: '34px !important' },
          }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={8}>
                <Box sx={{ bgcolor: '#fff', borderRadius: '10px', border: '2px solid #dbe3ea', p: 1.5 }}>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              {/* Employee Selection (searchable Autocomplete) */}
              <Grid item xs={12} md={5}>
                <h5 className='c-label'>Employee *</h5>
                <Controller
                  name="employeeId"
                  control={methods.control}
                  render={({ field }) => {
                    const empOptions = (employees || []).map((emp: any) => ({
                      label: `${emp.name || 'Unnamed'} (${emp.empId || '—'})`,
                      value: emp._id,
                      emp
                    }));
                    const selected = empOptions.find(o => o.value === field.value) || null;
                    return (
                      <>
                      <Autocomplete
                        size="small"
                        options={empOptions}
                        value={selected}
                        onChange={(_, opt) => {
                          const v = opt?.value || '';
                          field.onChange(v);
                          if (!v) {
                            setBasicSalary('');
                            setHra('');
                            setAdditionalEarnings([]);
                            setConfiguredDeductions([]);
                            setWorkingDays(0);
                            setPresentDays(0);
                            setLopDays(0);
                            setAttendanceSummary(null);
                            setTimesheetStatus(null);
                            setTimesheetTotalHours(0);
                            setAttendanceDays([]);
                            setLeaveByDate({});
                            setLeaveNameByDate({});
                            setHolidayNameByDate({});
                            setSalaryConfigLoaded(false);
                            setSelectedSalaryConfig(null);
                          }
                        }}
                        getOptionLabel={(opt) => opt?.label || ''}
                        isOptionEqualToValue={(opt, val) => opt.value === val.value}
                        filterOptions={(opts, state) => {
                          const q = state.inputValue.trim().toLowerCase();
                          if (!q) return opts;
                          return opts.filter(o => {
                            const e = o.emp || {};
                            return (
                              String(e.name || '').toLowerCase().includes(q) ||
                              String(e.empId || '').toLowerCase().includes(q) ||
                              String(e.email || '').toLowerCase().includes(q) ||
                              String(e.designation || '').toLowerCase().includes(q) ||
                              String(e.mobile || '').toLowerCase().includes(q)
                            );
                          });
                        }}
                        renderOption={(props, opt) => (
                          <Box component="li" {...props} key={opt.value} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.8 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{opt.emp?.name || 'Unnamed'}</Typography>
                            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                              {opt.emp?.empId || '—'}
                              {opt.emp?.designation ? ` · ${opt.emp.designation}` : ''}
                              {opt.emp?.email ? ` · ${opt.emp.email}` : ''}
                            </Typography>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className='c-text-field'
                            variant="standard"
                            placeholder={`Search employee (${empOptions.length})`}
                            size="small"
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                            error={Boolean(methods.formState.errors.employeeId)}
                          />
                        )}
                        noOptionsText={empOptions.length === 0 ? 'Loading employees...' : 'No match'}
                      />
                      {methods.formState.errors.employeeId && (
                        <Typography sx={{ color: '#d32f2f', fontSize: 12, mt: 0.5 }}>
                          {methods.formState.errors.employeeId?.message as string}
                        </Typography>
                      )}
                      </>
                    );
                  }}
                />
              </Grid>

              {/* Month Selection */}
              <Grid item xs={12} md={3}>
                <h5 className='c-label'>Salary Month *</h5>
                <Controller
                  name="month"
                  control={methods.control}
                  defaultValue={month}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="month"
                      className='c-text-field'
                      variant="standard"
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setMonth(e.target.value);
                      }}
                      InputProps={{ disableUnderline: true }}
                      size="small"
                      error={Boolean(methods.formState.errors.month)}
                      helperText={methods.formState.errors.month?.message as string}
                    />
                  )}
                />
              </Grid>

              {/* Cost Centre Selection - Only show if cost centres exist */}
              {costCentres && costCentres.length > 0 && (
                <Grid item xs={12} md={4}>
                  <h5 className='c-label'>Cost Centre</h5>
                  <TextField
                    select
                    fullWidth
                    className='c-text-field'
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    size="small"
                    value={selectedCostCentre}
                    onChange={(e: any) => setSelectedCostCentre(e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {costCentres.map((cc: any) => (
                      <MenuItem key={cc._id} value={cc._id}>{cc.alias ? `${cc.name} (${cc.alias})` : cc.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Working Days */}
              <Grid item xs={4}>
                <h5 className='c-label'>Working Days</h5>
                <TextField
                  fullWidth
                  type="text"
                  className='c-text-field'
                  variant="standard"
                  value={workingDays ? String(workingDays) : ''}
                  onChange={(e) => {
                    const normalized = normalizeWholeNumberInput(e.target.value);
                    const v = normalized === '' ? 0 : Number(normalized);
                    setWorkingDays(v);
                    setLopDays(Math.max(0, v - presentDays));
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  InputProps={{ disableUnderline: true }}
                  size="small"
                />
              </Grid>

              {/* Present Days */}
              <Grid item xs={4}>
                <h5 className='c-label'>Present Days</h5>
                <TextField
                  fullWidth
                  type="text"
                  className='c-text-field'
                  variant="standard"
                  value={presentDays ? String(presentDays) : ''}
                  onChange={(e) => {
                    const normalized = normalizeWholeNumberInput(e.target.value);
                    const v = normalized === '' ? 0 : Number(normalized);
                    setPresentDays(v);
                    setLopDays(Math.max(0, workingDays - v));
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  size="small"
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="View attendance report">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const empId = methods.getValues('employeeId');
                              if (!empId) return;
                              setAttendanceDrawerOpen(true);
                            }}
                          >
                            <CalendarMonthIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              {/* LOP Days (editable) */}
              <Grid item xs={4}>
                <h5 className='c-label'>Loss of Pay</h5>
                <TextField
                  fullWidth
                  type="text"
                  className='c-text-field'
                  variant="standard"
                  value={lopDays ? String(lopDays) : ''}
                  size="small"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  InputProps={{ disableUnderline: true }}
                  onChange={(e) => {
                    const normalized = e.target.value.replace(/[^0-9]/g, '');
                    const v = normalized === '' ? 0 : Math.min(Number(normalized), workingDays);
                    setLopDays(v);
                    setPresentDays(Math.max(0, workingDays - v));
                  }}
                  sx={{ '& .MuiInputBase-input': { color: lopDays > 0 ? '#dc2626' : '#374151', fontWeight: 600 } }}
                />
              </Grid>

              {!attendanceLoading && !attendanceSummary && methods.getValues('employeeId') && (
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    sx={{
                      py: 0.5,
                      fontSize: 12,
                      boxShadow: 'none',
                      filter: 'none',
                    }}
                  >
                    No attendance records found for {month}. Verify manually or check HRMS.
                  </Alert>
                </Grid>
              )}

              {/* Earnings + Deductions: full 2-column layout */}
              <Grid item xs={12}>
              <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2.5, p: 1.5, width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 0.75, mx: -1.5, px: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>Earnings</Typography>
                  {/* Add disabled — earnings come from Salary Config; edit there if needed */}
                  {/* <Button size="small" startIcon={<AddIcon />} onClick={handleAddAdditionalEarning} sx={{ textTransform: 'none', height: 28, fontSize: 12 }}>Add</Button> */}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {additionalEarnings.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 1 }}>
                      No allowances added — set them in Salary Config
                    </Typography>
                  )}
                  {additionalEarnings.map((earning, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-end' }}>
                      <Box sx={{ flex: 1 }}>
                        <h5 className='c-label'>{index === 0 ? 'Head' : ' '}</h5>
                        <Autocomplete
                          freeSolo
                          fullWidth
                          disabled
                          size="small"
                          options={earningHeadOptions}
                          value={earningHeadOptions.find((h: any) => String(h.headName || '').toLowerCase() === String(earning.label || '').toLowerCase()) || earning.label || ''}
                          getOptionLabel={(opt: any) => typeof opt === 'string' ? opt : (opt?.headName || '')}
                          isOptionEqualToValue={(opt: any, val: any) => {
                            const a = typeof opt === 'string' ? opt : opt?.headName;
                            const b = typeof val === 'string' ? val : val?.headName;
                            return String(a || '').toLowerCase() === String(b || '').toLowerCase();
                          }}
                          onInputChange={(_, val, reason) => { if (reason === 'input') handleAdditionalEarningChange(index, 'label', val); }}
                          onChange={(_, val: any) => handleAdditionalEarningChange(index, 'label', typeof val === 'string' ? val : (val?.headName || ''))}
                          renderOption={(props, opt: any) => (
                            <Box component="li" {...props} key={opt._id || opt.headName}>
                              <Box>
                                <Typography sx={{ fontSize: 13 }}>{opt.headName}</Typography>
                                {opt.headType?.name && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{opt.headType.name}</Typography>}
                              </Box>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} className='c-text-field-gray' variant="standard" placeholder="Search head"
                              InputProps={{ ...params.InputProps, disableUnderline: true }} size="small" />
                          )}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <h5 className='c-label'>{index === 0 ? 'Amount' : ' '}</h5>
                        <TextField
                          fullWidth
                          type="number"
                          disabled
                          className='c-text-field-gray'
                          variant="standard"
                          value={earning.amount}
                          onChange={(e) => handleAdditionalEarningChange(index, 'amount', e.target.value)}
                          size="small"
                          InputProps={{ disableUnderline: true, startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                        />
                      </Box>
                      {/* Remove disabled — manage earnings in Salary Config */}
                      {/* <IconButton onClick={() => handleRemoveAdditionalEarning(index)} color="error" size="small" sx={{ mb: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton> */}
                    </Box>
                  ))}
                </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2.5, p: 1.5, width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 0.75, mx: -1.5, px: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>Deductions</Typography>
                  {/* Add disabled — deductions come from Salary Config; edit there if needed */}
                  {/* <Button size="small" startIcon={<AddIcon />} onClick={handleAddDeduction} sx={{ textTransform: 'none', height: 28, fontSize: 12 }}>Add</Button> */}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  {configuredDeductions.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 1 }}>
                      No custom deductions added
                    </Typography>
                  )}
                  {configuredDeductions.map((deduction, index) => (
                    <Box key={`deduction-${index}`} sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-end' }}>
                      <Box sx={{ flex: 1 }}>
                        <h5 className='c-label'>{index === 0 ? 'Head' : ' '}</h5>
                      <Autocomplete
                        freeSolo
                        fullWidth
                        disabled
                        size="small"
                        options={deductionHeadOptions}
                        value={deductionHeadOptions.find((h: any) => String(h.headName || '').toLowerCase() === String(deduction.label || '').toLowerCase()) || deduction.label || ''}
                        getOptionLabel={(opt: any) => typeof opt === 'string' ? opt : (opt?.headName || '')}
                        onInputChange={(_, val, reason) => { if (reason === 'input') handleDeductionChange(index, 'label', val); }}
                        onChange={(_, val: any) => handleDeductionChange(index, 'label', typeof val === 'string' ? val : (val?.headName || ''))}
                        renderOption={(props, opt: any) => (
                          <Box component="li" {...props} key={opt._id || opt.headName}>
                            <Box>
                              <Typography sx={{ fontSize: 13 }}>{opt.headName}</Typography>
                              {opt.headType?.name && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{opt.headType.name}</Typography>}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} className='c-text-field-gray' variant="standard" placeholder="Search head"
                            InputProps={{ ...params.InputProps, disableUnderline: true }} size="small" />
                        )}
                      />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <h5 className='c-label'>{index === 0 ? 'Amount' : ' '}</h5>
                        <TextField
                          fullWidth
                          type="number"
                          disabled
                          className='c-text-field-gray'
                          variant="standard"
                          value={deduction.amount}
                          onChange={(e) => handleDeductionChange(index, 'amount', e.target.value)}
                          size="small"
                          InputProps={{ disableUnderline: true, startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                        />
                      </Box>
                      {/* Remove disabled — manage deductions in Salary Config */}
                      {/* <IconButton onClick={() => handleRemoveDeduction(index)} color="error" size="small" sx={{ mb: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton> */}
                    </Box>
                  ))}
                </Box>
                </Box>
              </Grid>
              </Grid>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
                {methods.getValues('employeeId') && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowExpenseAddons(!showExpenseAddons)}
                    disabled={approvedExpenses.length === 0}
                    sx={{
                      textTransform: 'none',
                      color: '#7c2d12',
                      borderColor: '#7c2d12',
                      '&:hover': { borderColor: '#7c2d12', backgroundColor: '#fff7ed' },
                      '&.Mui-disabled': { color: '#9ca3af', borderColor: '#d1d5db' }
                    }}
                  >
                    Expense Add-on ({approvedExpenses.length})
                    {selectedExpenseIds.size > 0 && ` — ${selectedExpenseIds.size} selected`}
                  </Button>
                )}
              </Grid>

              {/* Expense Add-on Table */}
              {approvedExpenses.length > 0 && (
                <>

                  {showExpenseAddons && (
                    <>
                      <Box sx={{ width: '100%', mt: 1, border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
                        {/* Header */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 80px 90px 80px', bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', px: 1.5, py: 0.6 }}>
                          <Box />
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Ref ID / Category</Typography>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Date</Typography>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textAlign: 'right' }}>Amount</Typography>
                          <Box />
                        </Box>
                        {approvedExpenses.map((exp, idx) => {
                          const checked = selectedExpenseIds.has(exp._id);
                          return (
                            <Box
                              key={exp._id}
                              onClick={() => {
                                const next = new Set(selectedExpenseIds);
                                checked ? next.delete(exp._id) : next.add(exp._id);
                                setSelectedExpenseIds(next);
                              }}
                              sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 80px 90px 80px', px: 1.5, py: 0.8, borderBottom: '1px solid #f3f4f6', cursor: 'pointer', bgcolor: checked ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafafa', '&:hover': { bgcolor: checked ? '#dbeafe' : '#f0f9ff' } }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <input type="checkbox" checked={checked} onChange={() => {}} style={{ cursor: 'pointer' }} />
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6366f1' }}>{exp.referenceId || `EXP-${String(exp.txnId || '').padStart(3,'0')}`}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{exp.expenseCategory?.name || '-'}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center' }}>
                                {exp.paymentDate ? new Date(exp.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                              </Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#065f46', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                ₹{Number(exp.settleAmount ?? exp.amount ?? 0).toLocaleString('en-IN')}
                              </Typography>
                              <Box />
                            </Box>
                          );
                        })}
                      </Box>
                      {selectedExpenseIds.size > 0 && (
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            <Typography fontSize={12}>
                              <strong>{selectedExpenseIds.size} expense(s)</strong> selected — Total: <strong>₹{
                                approvedExpenses
                                  .filter(e => selectedExpenseIds.has(e._id))
                                  .reduce((sum, e) => sum + (e.settleAmount ?? e.amount ?? 0), 0)
                                  .toLocaleString('en-IN')
                              }</strong>
                              {approvedExpenses.filter(e => selectedExpenseIds.has(e._id) && e.expTdsSelected?.apply).length > 0 && (
                                <> | TDS: <strong>₹{
                                  approvedExpenses
                                    .filter(e => selectedExpenseIds.has(e._id) && e.expTdsSelected?.apply)
                                    .reduce((sum, e) => sum + Number(e.expTdsSelected?.totalAmount || 0), 0)
                                    .toLocaleString('en-IN')
                                }</strong></>
                              )}
                              {' '}will be added to salary and marked as PAID
                            </Typography>
                          </Alert>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}



              {/* Gross Salary - Read Only */}
              <Grid item xs={12} sm={4}>
                <h5 className='c-label'>Gross Monthly Salary</h5>
                <TextField
                  fullWidth
                  type="number"
                  className='c-text-field'
                  variant="standard"
                  value={calculateGrossSalary()}
                  size="small"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                  }}
                  sx={{
                    '& .MuiInputBase-input': { fontWeight: 600, color: '#2e7d32' }
                  }}
                />
              </Grid>


              {/* Bank Account */}
              <Grid item xs={12} sm={4}>
                <h5 className='c-label'>From Bank Account</h5>
                <TextField
                  select
                  fullWidth
                  className='c-text-field'
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  size="small"
                  value={selectedFromBankAccount}
                  onChange={(e: any) => setSelectedFromBankAccount(e.target.value)}
                >
                  {fromBankAccountOptions.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Payment Status */}
              <Grid item xs={12} sm={4}>
                <h5 className='c-label'>Payment Status</h5>
                <TextField
                  select
                  fullWidth
                  className='c-text-field'
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  size="small"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                >
                  <MenuItem value="PROCESSED" disabled={paymentMode === 'CASH'}>
                    Processed {paymentMode === 'CASH' && '(Not available for Cash)'}
                  </MenuItem>
                  <MenuItem value="PAID">Paid</MenuItem>
                </TextField>
              </Grid>

              {/* Payment Mode Radio Group */}
              <Grid item xs={12}>
                <RadioGroup
                  row
                  aria-labelledby="payment-mode-label"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  sx={{
                    '& .MuiFormControlLabel-root': {
                      mr: 2,
                      ml: 0,
                      '& .MuiRadio-root': {
                        p: 0.5,
                        mr: 0.5,
                        color: '#6366f1',
                        '&.Mui-checked': {
                          color: '#6366f1',
                        }
                      },
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem'
                      }
                    }
                  }}
                >
                  <FormControlLabel value="CASH" control={<Radio size="small" />} label="Cash" />
                  <FormControlLabel value="NEFT" control={<Radio size="small" />} label="NEFT" />
                  <FormControlLabel value="RTGS" control={<Radio size="small" />} label="RTGS" />
                  <FormControlLabel value="UPI" control={<Radio size="small" />} label="UPI" />
                  <FormControlLabel value="CHEQUE" control={<Radio size="small" />} label="Cheque" />
                  <FormControlLabel value="NETBANKING" control={<Radio size="small" />} label="Net Banking" />
                </RadioGroup>
              </Grid>

              {/* Transaction Reference */}
              {paymentMode !== 'CASH' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={paymentMode === 'CHEQUE' ? "Cheque Number" :
                      paymentMode === 'UPI' ? "UPI ID" :
                        "Transaction Ref"}
                    value={transactionRef}
                    onChange={(e) => setTransactionRef(e.target.value)}
                  />
                </Grid>
              )}


              {/* Remarks */}
              <Grid item xs={12}>
                <h5 className='c-label'>Remarks</h5>
                <Controller
                  name="remarks"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      className='c-text-field'
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      multiline
                      rows={1}
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'none' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f8fafc', border: '1px solid #dbe3ea', borderRadius: '6px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
                    Payable Summary
                  </Typography>
                  <Grid container spacing={1.2}>
                    <Grid item xs={6} md={3}>
                      <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Gross Salary</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#065f46' }}>
                        ₹{calculateGrossSalary().toLocaleString('en-IN')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Manual Deductions</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#b45309' }}>
                        ₹{calculateConfiguredDeductions().toLocaleString('en-IN')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                        {salaryCalculation ? 'Calculated Net Salary' : 'Estimated Net Salary'}
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1d4ed8' }}>
                        ₹{(salaryCalculation?.summary?.netSalary ?? calculateManualNetSalary()).toLocaleString('en-IN')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Employee Will Receive</Typography>
                      <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#059669' }}>
                        ₹{calculateManualPayable().toLocaleString('en-IN')}
                      </Typography>
                    </Grid>

                    {/* Statutory deduction row — shows configured rates even before Calculate click */}
                    {(applyTax || salaryCalculation) && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 0.5 }} />
                          <Typography sx={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.5 }}>
                            Statutory Deductions {salaryCalculation ? '' : '(estimate — click Calculate for exact)'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>PF {applyPF ? '(applied)' : '(skipped)'}</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: applyPF ? '#b91c1c' : '#9ca3af' }}>
                            ₹{Number(salaryCalculation?.deductions?.pf || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>ESI {applyESI ? '(applied)' : '(skipped)'}</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: applyESI ? '#b91c1c' : '#9ca3af' }}>
                            ₹{Number(salaryCalculation?.deductions?.esi || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Prof. Tax {applyPT ? '(applied)' : '(skipped)'}</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: applyPT ? '#b91c1c' : '#9ca3af' }}>
                            ₹{Number(salaryCalculation?.deductions?.professionalTax || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>TDS {applyTDS ? `(${tdsSelected.rate || 0}%)` : '(skipped)'}</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: applyTDS ? '#b91c1c' : '#9ca3af' }}>
                            ₹{Number(salaryCalculation?.deductions?.tds || 0).toLocaleString('en-IN')}
                          </Typography>
                        </Grid>

                        {/* Loan EMI auto-deducted from active loans */}
                        {(() => {
                          const ded: any = (salaryCalculation as any)?.deductions || {};
                          const loanTotal = Number(ded.loan || 0);
                          const loanDetails: any[] = Array.isArray(ded.loanDetails) ? ded.loanDetails : [];
                          if (loanTotal <= 0) return null;
                          return (
                            <Grid item xs={12}>
                              <Box sx={{ mt: 0.5, p: 1, bgcolor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#9a3412' }}>
                                    Loan EMI Deduction
                                  </Typography>
                                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#9a3412' }}>
                                    ₹{loanTotal.toLocaleString('en-IN')}
                                  </Typography>
                                </Box>
                                {loanDetails.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                    {loanDetails.map((l: any, i: number) => (
                                      <Typography key={i} sx={{ fontSize: 11, color: '#7c2d12' }}>
                                        {l.loanType || 'Loan'}: ₹{Number(l.emi).toLocaleString('en-IN')} ({l.paidMonths}/{l.totalMonths})
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          );
                        })()}
                      </>
                    )}
                  </Grid>
                  {expenseAddOnTotal > 0 && (
                    <Typography sx={{ mt: 1, fontSize: 12, color: '#92400e' }}>
                      Includes expense add-on ₹{expenseAddOnTotal.toLocaleString('en-IN')}.
                    </Typography>
                  )}
                </Box>
              </Grid>

            </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  border: '2px solid #dbe3ea',
                  p: 1.5,
                  position: 'sticky',
                  top: 0
                }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#111827', mb: 1, pb: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                    PAYABLE SUMMARY
                  </Typography>

                  {!selectedSalaryConfig ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>Select employee to see summary</Typography>
                    </Box>
                  ) : (
                  <>
                  {/* Salary Config Summary */}
                  {selectedSalaryConfig && (
                    <Box sx={{ mb: 1.2, pb: 1, borderBottom: '1px solid #e5e7eb' }}>
                      <Typography sx={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Salary Config</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Annual CTC</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>₹{getDecimalValue(selectedSalaryConfig.ctcAmount).toLocaleString('en-IN')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Monthly Gross</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>₹{calculateGrossSalary().toLocaleString('en-IN')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Per Day Rate</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>₹{perDayRate().toFixed(2)}</Typography>
                      </Box>
                      {lopDays > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                          <Typography sx={{ fontSize: 11, color: '#dc2626' }}>LOP ({lopDays} days)</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#dc2626' }}>−₹{calculateLOPDeduction().toLocaleString('en-IN')}</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {/* LOP Calculation Card */}
                  {lopDays > 0 && (
                    <Box sx={{ mb: 1.2, p: 1, bgcolor: lopDays > 0 ? '#fff7ed' : '#f0fdf4', border: `1px solid ${lopDays > 0 ? '#fed7aa' : '#bbf7d0'}`, borderRadius: 1 }}>
                      <Typography sx={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.6 }}>LOP Calculation</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Present / Working</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{presentDays} / {workingDays} days</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>LOP Days</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: lopDays > 0 ? '#dc2626' : '#16a34a' }}>{lopDays} days</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Per Day Rate</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>₹{perDayRate().toFixed(2)}</Typography>
                      </Box>
                      {lopDays > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #fed7aa' }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>LOP Deduction</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>−₹{calculateLOPDeduction().toLocaleString('en-IN')}</Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Payable Breakdown */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Gross Salary</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>₹{calculateGrossSalary().toLocaleString('en-IN')}</Typography>
                  </Box>
                  {lopDays > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontSize: 12, color: '#dc2626' }}>− LOP ({lopDays} days)</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>−₹{calculateLOPDeduction().toLocaleString('en-IN')}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: '#6b7280' }}>− Deductions</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>−₹{calculateConfiguredDeductions().toLocaleString('en-IN')}</Typography>
                  </Box>
                  {expenseAddOnTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontSize: 12, color: '#7c3aed' }}>+ Expense Add-on ({selectedExpenseIds.size})</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>+₹{expenseAddOnTotal.toLocaleString('en-IN')}</Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 0.8, borderTop: '2px solid #059669', bgcolor: '#f0fdf4', p: 1, borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#065f46' }}>Net Take Home</Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#065f46' }}>₹{calculateManualPayable().toLocaleString('en-IN')}</Typography>
                  </Box>

                  <Box sx={{ mt: 1, p: 0.8, bgcolor: '#f9fafb', borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Period: {month}</Typography>
                    {!attendanceSummary && (
                      <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>Attendance: not available</Typography>
                    )}
                  </Box>
                  </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

        </Box>
      </FormProvider>

      <MuiDrawer
        anchor="right"
        open={attendanceDrawerOpen}
        onClose={() => setAttendanceDrawerOpen(false)}
        PaperProps={{ sx: { width: 420 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, bgcolor: '#e9ecf1', borderBottom: '1px solid #d1d5db' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            {(() => {
              const [yy, mm] = month.split('-').map(Number);
              const label = (yy && mm) ? new Date(yy, mm - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : month;
              return `Attendance Report for ${label}`;
            })()}
          </Typography>
          <IconButton size="small" onClick={() => setAttendanceDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflowY: 'auto' }}>
          {!attendanceSummary && !attendanceLoading && (
            <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>No attendance records for {month}</Alert>
          )}
          {attendanceLoading && (
            <Typography sx={{ fontSize: 12, color: '#6b7280', textAlign: 'center', py: 2 }}>Loading attendance...</Typography>
          )}

          {/* Timesheet status + month summary */}
          {(timesheetStatus || attendanceSummary) && (
            <Box sx={{ mb: 2 }}>
              {timesheetStatus && (() => {
                const s = timesheetStatus.toLowerCase();
                const c = s === 'approved' || s === 'closed' || s === 'invoiced'
                  ? { bg: '#dcfce7', color: '#166534' }
                  : s === 'pending'
                    ? { bg: '#fef9c3', color: '#854d0e' }
                    : s === 'rejected'
                      ? { bg: '#fee2e2', color: '#991b1b' }
                      : { bg: '#e5e7eb', color: '#374151' };
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Timesheet Status</Typography>
                    <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: c.bg }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.color }}>{timesheetStatus}</Typography>
                    </Box>
                  </Box>
                );
              })()}
              {attendanceSummary && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                  {[
                    { label: 'Present', value: attendanceSummary.present, color: '#166534' },
                    { label: 'Absent', value: attendanceSummary.absent, color: '#991b1b' },
                    { label: 'Leave', value: attendanceSummary.leave, color: '#1e40af' },
                    { label: 'Half-Day', value: attendanceSummary.halfDay, color: '#854d0e' },
                    { label: 'Week-Off', value: attendanceSummary.weekOff, color: '#374151' },
                    { label: 'Holiday', value: attendanceSummary.holiday, color: '#374151' },
                    // Comp-Off tile only when the month actually has comp-offs
                    ...(attendanceSummary.compOff > 0 ? [{ label: 'Comp-Off', value: attendanceSummary.compOff, color: '#6b21a8' }] : []),
                  ].map(item => (
                    <Box key={item.label} sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 0.8, textAlign: 'center', bgcolor: '#fff' }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: item.color, lineHeight: 1.2 }}>{item.value}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#6b7280' }}>{item.label}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {timesheetTotalHours > 0 && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.6, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1 }}>
                  <Typography sx={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>Total Hours Logged</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#166534' }}>{timesheetTotalHours.toFixed(1)} h</Typography>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Day-wise</Typography>
            <Tooltip
              arrow
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>Legend</Typography>
                  {[
                    { code: 'P', label: 'Present', bg: '#dcfce7', color: '#166534', pay: 'Paid' },
                    { code: 'HD', label: 'Half Day', bg: '#fef9c3', color: '#854d0e', pay: '½ Paid' },
                    { code: 'A', label: 'Absent', bg: '#fee2e2', color: '#991b1b', pay: 'Unpaid' },
                    { code: 'L', label: 'Leave (approved)', bg: '#dbeafe', color: '#1e40af', pay: 'Paid' },
                    { code: 'OT', label: 'Overtime', bg: '#dcfce7', color: '#166534', pay: 'Paid' },
                    { code: 'WO', label: 'Week Off', bg: '#f3f4f6', color: '#374151', pay: 'Paid' },
                    { code: 'CO', label: 'Comp Off', bg: '#f3e8ff', color: '#6b21a8', pay: 'Paid' },
                    { code: 'H', label: 'Holiday', bg: '#f3f4f6', color: '#374151', pay: 'Paid' },
                    { code: 'OH', label: 'Optional Holiday', bg: '#f3f4f6', color: '#374151', pay: 'Paid' },
                  ].map(it => (
                    <Box key={it.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 11, mb: 0.25 }}>
                      <Box sx={{ width: 18, height: 16, borderRadius: 0.5, bgcolor: it.bg, border: '1px solid rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: 9, color: it.color }}>{it.code}</span>
                      </Box>
                      <span style={{ flex: 1 }}>{it.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, color: it.pay === 'Unpaid' ? '#fca5a5' : '#86efac' }}>{it.pay}</span>
                    </Box>
                  ))}
                  <Typography sx={{ fontSize: 10, mt: 0.75, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.25)' }}>
                    Pending/rejected leave is unpaid and shown as Absent.
                  </Typography>
                  <Typography sx={{ fontSize: 10, mt: 0.25 }}>
                    {`Week off: ${Array.isArray(empWeekOffDays) && empWeekOffDays.length > 0
                      ? empWeekOffDays.map((w: any) => {
                          const weeks: number[] = Array.isArray(w.weeks) ? w.weeks : [];
                          const allWeeks = weeks.length === 0 || weeks.length >= 5;
                          return allWeeks ? w.day : `${w.day} (W${weeks.join(',')})`;
                        }).join(', ')
                      : 'Sunday (default — no pattern assigned)'}`}
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" sx={{ p: 0.25 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <Typography key={d} sx={{ fontSize: 10, fontWeight: 700, textAlign: 'center', color: '#6b7280' }}>{d}</Typography>
            ))}
            {(() => {
              const [yy, mm] = month.split('-').map(Number);
              if (!yy || !mm) return null;
              // Sunday-first grid — same layout as the mobile app calendar
              const offset = new Date(yy, mm - 1, 1).getDay();
              const totalDays = new Date(yy, mm, 0).getDate();
              const cells: any[] = [];
              for (let i = 0; i < offset; i++) cells.push(<Box key={`b-${i}`} />);
              for (let d = 1; d <= totalDays; d++) {
                const dateStr = `${yy}-${String(mm).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                const rec = attendanceDays.find((x: any) => String(x.date).slice(0,10) === dateStr);
                let t = String(rec?.type || '').trim();
                // Normalize comp-off spelling variants (Comp-Off / Comp Off / CompOff)
                if (/^comp[\s-]?off$/i.test(t)) t = 'CompOff';
                // Only approved leave shows as Leave. Pending/rejected leave = unpaid → shown as Absent.
                // Prefer the leave-request map (app source of truth), fall back to day's leaveStatus.
                if (t === 'Leave') {
                  const ls = (leaveByDate[dateStr] || rec?.leaveStatus || '').toLowerCase();
                  if (ls !== 'approved') t = 'Absent';
                }
                // Holiday-config day with no attendance record → still show it as a Holiday
                // (matches the app calendar, which lists holidays from the holiday config).
                const hName = holidayNameByDate[dateStr];
                if (!t && hName) t = 'Holiday';
                // No explicit record → week-off from the employee's assigned pattern
                // (Sunday default when no pattern) — matches the summary count
                if (!t && isWeekOffDate(yy, mm, d, empWeekOffDays)) t = 'WeekOff';
                const colorMap: any = {
                  'Present': { bg: '#dcfce7', color: '#166534' },
                  'WFH': { bg: '#dcfce7', color: '#166534' },
                  'Overtime': { bg: '#dcfce7', color: '#166534' },
                  'Half-Day': { bg: '#fef9c3', color: '#854d0e' },
                  'Absent': { bg: '#fee2e2', color: '#991b1b' },
                  'Leave': { bg: '#dbeafe', color: '#1e40af' },
                  'Holiday': { bg: '#f3f4f6', color: '#374151' },
                  'WeekOff': { bg: '#f3f4f6', color: '#374151' },
                  'Optional-Holiday': { bg: '#f3f4f6', color: '#374151' },
                  'CompOff': { bg: '#f3e8ff', color: '#6b21a8' }
                };
                const codeMap: any = {
                  'Present': 'P', 'WFH': 'P', 'Overtime': 'OT', 'Half-Day': 'HD',
                  'Absent': 'A', 'Leave': 'L', 'Holiday': 'H', 'WeekOff': 'WO',
                  'Optional-Holiday': 'OH', 'CompOff': 'CO'
                };
                const c = colorMap[t] || { bg: '#fff', color: '#9ca3af' };
                // Tooltip shows the leave/holiday name when we have it (e.g. "Holiday · Diwali").
                const tipName = (t === 'Holiday' || t === 'Optional-Holiday')
                  ? hName
                  : (t === 'Leave' ? leaveNameByDate[dateStr] : '');
                let tip = t ? (tipName ? `${t} · ${tipName}` : t) : 'No record';
                // A day shown as Absent may still have a (pending/rejected) leave request —
                // surface which leave was applied and its status.
                if (t === 'Absent' && leaveByDate[dateStr] && leaveByDate[dateStr] !== 'approved') {
                  tip = `Absent · ${leaveNameByDate[dateStr] || 'Leave'} (${leaveByDate[dateStr]})`;
                }
                // Append the reason the employee gave while applying for leave.
                const tipReason = leaveReasonByDate[dateStr];
                if (tipReason && (t === 'Leave' || (t === 'Absent' && leaveByDate[dateStr]))) {
                  tip = `${tip} — "${tipReason}"`;
                }
                cells.push(
                  <Tooltip key={d} title={tip}>
                    <Box sx={{ aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: c.bg, border: '1px solid #e5e7eb', borderRadius: 0.5, cursor: 'pointer' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.color }}>{d}</Typography>
                      {t && <Typography sx={{ fontSize: 9, fontWeight: 700, color: c.color }}>{codeMap[t] || t.slice(0,2)}</Typography>}
                    </Box>
                  </Tooltip>
                );
              }
              return cells;
            })()}
          </Box>

          {/* Timesheet Detail Table */}
          {attendanceDays.length > 0 && (
            <Box sx={{ px: 0, pt: 2, pb: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1, color: '#374151' }}>Timesheet Details</Typography>
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>Day</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>Check In</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>Check Out</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>Hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceDays
                      .slice()
                      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                      .map((rec: any, i: number) => {
                        const colorMap: any = {
                          'Present': '#166534', 'WFH': '#166534', 'Overtime': '#166534',
                          'Half-Day': '#854d0e', 'Absent': '#991b1b', 'Leave': '#1e40af',
                          'Holiday': '#374151', 'WeekOff': '#374151', 'Optional-Holiday': '#374151'
                        };
                        let t = String(rec.type || '').trim();
                        const isLeaveRow = t === 'Leave';
                        // Only approved leave shows as Leave; pending/rejected → Absent (unpaid).
                        if (t === 'Leave' && String(rec.leaveStatus || '').toLowerCase() !== 'approved') t = 'Absent';
                        // Name of the leave type the employee applied for, if any.
                        const dKey = String(rec.date).slice(0, 10);
                        const leaveName = isLeaveRow ? leaveNameByDate[dKey] : '';
                        // Holiday name from the holiday config (e.g. "Diwali"), if this day is one.
                        const holidayName = (t === 'Holiday' || t === 'Optional-Holiday') ? holidayNameByDate[dKey] : '';
                        // Reason the employee gave while applying for the leave.
                        const leaveReason = isLeaveRow ? leaveReasonByDate[dKey] : '';
                        const leaveLabel = leaveName
                          ? (leaveReason ? `${leaveName} — "${leaveReason}"` : leaveName)
                          : (leaveReason ? `"${leaveReason}"` : '');
                        const subLabel = leaveLabel || holidayName || rec.label || '';
                        const color = colorMap[t] || '#9ca3af';
                        const fmt = (d: any) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
                        const hrs = rec.hours || rec.regularHours || 0;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '5px 8px', color: '#111827' }}>{new Date(rec.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                            <td style={{ padding: '5px 8px', color: '#6b7280' }}>{rec.day || new Date(rec.date).toLocaleDateString('en-IN', { weekday: 'short' })}</td>
                            <td style={{ padding: '5px 8px' }}>
                              <span style={{ color, fontWeight: 600 }}>{t || '—'}</span>
                              {subLabel && <span style={{ color: '#9ca3af', fontSize: 10, display: 'block' }}>{subLabel}</span>}
                            </td>
                            <td style={{ padding: '5px 8px', color: '#374151' }}>{fmt(rec.checkIn)}</td>
                            <td style={{ padding: '5px 8px', color: '#374151' }}>{fmt(rec.checkOut)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: hrs > 0 ? '#059669' : '#9ca3af', fontWeight: hrs > 0 ? 600 : 400 }}>{hrs > 0 ? `${hrs}h` : '—'}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Box>
      </MuiDrawer>
    </div>
  );
};

export default DialogProcessSalaryForm;
